import React, { useState } from 'react';
import { X, Users, CheckCircle, BookOpen, FileText, Link as LinkIcon, ArrowRight, ExternalLink } from 'lucide-react';
type ExampleType = 'connections' | 'workflow' | 'resources' | 'hybrid';
interface ExamplesModalProps {
  onClose: () => void;
}
const ExamplesModal: React.FC<ExamplesModalProps> = ({
  onClose
}) => {
  const [activeExample, setActiveExample] = useState<ExampleType>('connections');
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-489">
      <div className="bg-white rounded-lg shadow-xl w-4/5 max-w-4xl h-4/5 flex flex-col" data-id="element-490">
        <div className="p-4 border-b flex justify-between items-center" data-id="element-491">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-492">
            Growth Agent Examples
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-493">
            <X className="w-5 h-5" data-id="element-494" />
          </button>
        </div>
        <div className="flex flex-1 overflow-hidden" data-id="element-495">
          {/* Sidebar with example categories */}
          <div className="w-64 border-r bg-gray-50 p-4 overflow-y-auto" data-id="element-496">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-497">
              How to use the Growth Agent
            </h4>
            <div className="space-y-1" data-id="element-498">
              <button onClick={() => setActiveExample('connections')} className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center ${activeExample === 'connections' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`} data-id="element-499">
                <Users className="w-4 h-4 mr-2" data-id="element-500" />
                <span data-id="element-501">Peer Connections</span>
              </button>
              <button onClick={() => setActiveExample('workflow')} className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center ${activeExample === 'workflow' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`} data-id="element-502">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-503" />
                <span data-id="element-504">Guided Workflows</span>
              </button>
              <button onClick={() => setActiveExample('resources')} className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center ${activeExample === 'resources' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`} data-id="element-505">
                <BookOpen className="w-4 h-4 mr-2" data-id="element-506" />
                <span data-id="element-507">Learning Resources</span>
              </button>
              <button onClick={() => setActiveExample('hybrid')} className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center ${activeExample === 'hybrid' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`} data-id="element-508">
                <FileText className="w-4 h-4 mr-2" data-id="element-509" />
                <span data-id="element-510">Hybrid Responses</span>
              </button>
            </div>
          </div>
          {/* Main content area */}
          <div className="flex-1 p-6 overflow-y-auto" data-id="element-511">
            {activeExample === 'connections' && <div data-id="element-512">
                <h2 className="text-xl font-medium text-gray-900 mb-4" data-id="element-513">
                  Peer-to-Peer Connection Suggestions
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-514">
                  The Growth Agent can suggest relevant connections based on
                  your career goals and upward mobility paths.
                </p>
                <div className="border rounded-lg overflow-hidden mb-6" data-id="element-515">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-516">
                    <p className="text-sm font-medium" data-id="element-517">Example Query</p>
                  </div>
                  <div className="p-4 bg-blue-50" data-id="element-518">
                    <p className="text-blue-800" data-id="element-519">
                      "I want to move into a Senior Engineering Manager role.
                      Who should I connect with to learn more about this path?"
                    </p>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden" data-id="element-520">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-521">
                    <p className="text-sm font-medium" data-id="element-522">Agent Response</p>
                  </div>
                  <div className="p-4" data-id="element-523">
                    <div className="flex items-start mb-3" data-id="element-524">
                      <div className="bg-blue-100 p-1.5 rounded-full mr-3" data-id="element-525">
                        <Users className="w-5 h-5 text-blue-600" data-id="element-526" />
                      </div>
                      <div data-id="element-527">
                        <p className="text-gray-800 mb-3" data-id="element-528">
                          Based on your current role and career goals, I've
                          identified several potential connections who could
                          provide valuable insights on the Senior Engineering
                          Manager path:
                        </p>
                        <div className="space-y-4 mb-4" data-id="element-529">
                          <div className="bg-white border rounded-md p-3" data-id="element-530">
                            <div className="flex items-center" data-id="element-531">
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-medium mr-3" data-id="element-532">
                                JL
                              </div>
                              <div data-id="element-533">
                                <p className="font-medium" data-id="element-534">Jennifer Liu</p>
                                <p className="text-sm text-gray-600" data-id="element-535">
                                  Senior Engineering Manager, Platform Team
                                </p>
                                <div className="flex items-center mt-1" data-id="element-536">
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-id="element-537">
                                    95% match
                                  </span>
                                  <span className="text-xs text-gray-500 ml-2" data-id="element-538">
                                    3 mutual connections
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 text-sm" data-id="element-539">
                              <p data-id="element-540">
                                Jennifer has been in her role for 3 years and
                                previously followed a similar career path to
                                yours.
                              </p>
                            </div>
                          </div>
                          <div className="bg-white border rounded-md p-3" data-id="element-541">
                            <div className="flex items-center" data-id="element-542">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-medium mr-3" data-id="element-543">
                                MR
                              </div>
                              <div data-id="element-544">
                                <p className="font-medium" data-id="element-545">Michael Rodriguez</p>
                                <p className="text-sm text-gray-600" data-id="element-546">
                                  Director of Engineering
                                </p>
                                <div className="flex items-center mt-1" data-id="element-547">
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-id="element-548">
                                    87% match
                                  </span>
                                  <span className="text-xs text-gray-500 ml-2" data-id="element-549">
                                    Hiring manager
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 text-sm" data-id="element-550">
                              <p data-id="element-551">
                                Michael oversees several engineering teams and
                                has experience mentoring new managers.
                              </p>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-800" data-id="element-552">
                          Would you like me to facilitate an introduction to any
                          of these connections? I can also suggest specific
                          topics you might want to discuss with them.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
            {activeExample === 'workflow' && <div data-id="element-553">
                <h2 className="text-xl font-medium text-gray-900 mb-4" data-id="element-554">
                  Guided Workflows with Feedback
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-555">
                  The Growth Agent can guide you through complex tasks
                  step-by-step, providing feedback at each stage.
                </p>
                <div className="border rounded-lg overflow-hidden mb-6" data-id="element-556">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-557">
                    <p className="text-sm font-medium" data-id="element-558">Example Query</p>
                  </div>
                  <div className="p-4 bg-blue-50" data-id="element-559">
                    <p className="text-blue-800" data-id="element-560">
                      "I need to prepare for my first performance review as a
                      team lead. Can you help me structure this?"
                    </p>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden" data-id="element-561">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-562">
                    <p className="text-sm font-medium" data-id="element-563">Guided Workflow</p>
                  </div>
                  <div className="p-4" data-id="element-564">
                    <div className="space-y-6" data-id="element-565">
                      <div className="flex items-start" data-id="element-566">
                        <div className="bg-blue-100 p-1.5 rounded-full mr-3 flex-shrink-0" data-id="element-567">
                          <CheckCircle className="w-5 h-5 text-blue-600" data-id="element-568" />
                        </div>
                        <div data-id="element-569">
                          <p className="font-medium mb-2" data-id="element-570">
                            Step 1: Self-Assessment
                          </p>
                          <p className="text-gray-700 mb-3" data-id="element-571">
                            Let's start by identifying your key achievements
                            since taking on the team lead role. What are 3-5
                            major accomplishments you're proud of?
                          </p>
                          <div className="bg-gray-50 border rounded p-3 text-sm mb-3" data-id="element-572">
                            <p className="italic text-gray-600" data-id="element-573">
                              User response would go here...
                            </p>
                          </div>
                          <div className="bg-green-50 border border-green-100 rounded p-3 text-sm" data-id="element-574">
                            <p className="text-green-800" data-id="element-575">
                              <span className="font-medium" data-id="element-576">Feedback:</span>{' '}
                              Great examples! I notice these focus primarily on
                              technical achievements. Consider also including
                              achievements related to team development and
                              process improvements, which are key aspects of
                              leadership roles.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start" data-id="element-577">
                        <div className="bg-blue-100 p-1.5 rounded-full mr-3 flex-shrink-0" data-id="element-578">
                          <CheckCircle className="w-5 h-5 text-blue-600" data-id="element-579" />
                        </div>
                        <div data-id="element-580">
                          <p className="font-medium mb-2" data-id="element-581">
                            Step 2: Areas for Growth
                          </p>
                          <p className="text-gray-700 mb-3" data-id="element-582">
                            Now let's identify areas where you'd like to grow as
                            a team lead. What challenges have you faced, and
                            what skills would you like to develop further?
                          </p>
                          <div className="bg-gray-50 border rounded p-3 text-sm mb-3" data-id="element-583">
                            <p className="italic text-gray-600" data-id="element-584">
                              User response would go here...
                            </p>
                          </div>
                          <div className="bg-green-50 border border-green-100 rounded p-3 text-sm" data-id="element-585">
                            <p className="text-green-800" data-id="element-586">
                              <span className="font-medium" data-id="element-587">Feedback:</span>{' '}
                              These are thoughtful areas for growth. Being
                              specific about how these challenges manifested
                              will help your manager provide targeted support.
                              Consider framing these as opportunities rather
                              than weaknesses.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start opacity-70" data-id="element-588">
                        <div className="bg-gray-100 p-1.5 rounded-full mr-3 flex-shrink-0" data-id="element-589">
                          <CheckCircle className="w-5 h-5 text-gray-600" data-id="element-590" />
                        </div>
                        <div data-id="element-591">
                          <p className="font-medium mb-2" data-id="element-592">
                            Step 3: Team Performance
                          </p>
                          <p className="text-gray-700 mb-3" data-id="element-593">
                            Let's discuss your team's overall performance. What
                            metrics improved under your leadership? Where did
                            the team excel or struggle?
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start opacity-50" data-id="element-594">
                        <div className="bg-gray-100 p-1.5 rounded-full mr-3 flex-shrink-0" data-id="element-595">
                          <CheckCircle className="w-5 h-5 text-gray-600" data-id="element-596" />
                        </div>
                        <div data-id="element-597">
                          <p className="font-medium mb-2" data-id="element-598">
                            Step 4: Goals and Vision
                          </p>
                          <p className="text-gray-700" data-id="element-599">
                            Finally, we'll prepare your goals for the next
                            period and your vision for the team.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
            {activeExample === 'resources' && <div data-id="element-600">
                <h2 className="text-xl font-medium text-gray-900 mb-4" data-id="element-601">
                  Learning Resources
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-602">
                  The Growth Agent can recommend personalized learning resources
                  from various sources, including LinkedIn Learning and internal
                  company courses.
                </p>
                <div className="border rounded-lg overflow-hidden mb-6" data-id="element-603">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-604">
                    <p className="text-sm font-medium" data-id="element-605">Example Query</p>
                  </div>
                  <div className="p-4 bg-blue-50" data-id="element-606">
                    <p className="text-blue-800" data-id="element-607">
                      "I want to improve my data visualization skills for
                      presenting technical information to non-technical
                      stakeholders."
                    </p>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden" data-id="element-608">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-609">
                    <p className="text-sm font-medium" data-id="element-610">Agent Response</p>
                  </div>
                  <div className="p-4" data-id="element-611">
                    <div className="flex items-start" data-id="element-612">
                      <div className="bg-blue-100 p-1.5 rounded-full mr-3" data-id="element-613">
                        <BookOpen className="w-5 h-5 text-blue-600" data-id="element-614" />
                      </div>
                      <div data-id="element-615">
                        <p className="text-gray-800 mb-4" data-id="element-616">
                          I've compiled a list of resources to help you improve
                          your data visualization skills for non-technical
                          audiences. These are tailored to your current skill
                          level and role:
                        </p>
                        <div className="space-y-4 mb-6" data-id="element-617">
                          <div className="border rounded-md overflow-hidden" data-id="element-618">
                            <div className="bg-blue-50 px-3 py-2 border-b" data-id="element-619">
                              <p className="font-medium text-blue-800" data-id="element-620">
                                LinkedIn Learning Courses
                              </p>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-621">
                              <div className="flex items-start" data-id="element-622">
                                <ExternalLink className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-623" />
                                <div data-id="element-624">
                                  <p className="font-medium" data-id="element-625">
                                    Data Visualization: Storytelling
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-626">
                                    Learn how to craft compelling narratives
                                    with data
                                  </p>
                                  <div className="flex items-center mt-1" data-id="element-627">
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-628">
                                      4.8/5 rating
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2" data-id="element-629">
                                      2.5 hours
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-630">
                                <ExternalLink className="w-4 h-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-631" />
                                <div data-id="element-632">
                                  <p className="font-medium" data-id="element-633">
                                    Translating Technical Information
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-634">
                                    Techniques for communicating complex topics
                                    to diverse audiences
                                  </p>
                                  <div className="flex items-center mt-1" data-id="element-635">
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-636">
                                      4.6/5 rating
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2" data-id="element-637">
                                      1.8 hours
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="border rounded-md overflow-hidden" data-id="element-638">
                            <div className="bg-purple-50 px-3 py-2 border-b" data-id="element-639">
                              <p className="font-medium text-purple-800" data-id="element-640">
                                Internal Company Courses
                              </p>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-641">
                              <div className="flex items-start" data-id="element-642">
                                <LinkIcon className="w-4 h-4 text-purple-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-643" />
                                <div data-id="element-644">
                                  <p className="font-medium" data-id="element-645">
                                    Executive Communication Workshop
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-646">
                                    Next session: June 15, 2023 (Virtual)
                                  </p>
                                  <div className="flex items-center mt-1" data-id="element-647">
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-id="element-648">
                                      Highly recommended
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2" data-id="element-649">
                                      4 hours
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-650">
                                <LinkIcon className="w-4 h-4 text-purple-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-651" />
                                <div data-id="element-652">
                                  <p className="font-medium" data-id="element-653">
                                    Data Visualization Tools Training
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-654">
                                    On-demand course covering Tableau and
                                    PowerBI
                                  </p>
                                  <div className="flex items-center mt-1" data-id="element-655">
                                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full" data-id="element-656">
                                      Self-paced
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2" data-id="element-657">
                                      6 modules
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="border rounded-md overflow-hidden" data-id="element-658">
                            <div className="bg-orange-50 px-3 py-2 border-b" data-id="element-659">
                              <p className="font-medium text-orange-800" data-id="element-660">
                                Books & Articles
                              </p>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-661">
                              <div className="flex items-start" data-id="element-662">
                                <FileText className="w-4 h-4 text-orange-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-663" />
                                <div data-id="element-664">
                                  <p className="font-medium" data-id="element-665">
                                    "Storytelling with Data" by Cole Nussbaumer
                                    Knaflic
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-666">
                                    Available in company library (3 copies)
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start" data-id="element-667">
                                <FileText className="w-4 h-4 text-orange-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-668" />
                                <div data-id="element-669">
                                  <p className="font-medium" data-id="element-670">
                                    HBR: "Visualizations That Really Work"
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-671">
                                    Accessible through company HBR subscription
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-800" data-id="element-672">
                          Based on your schedule, I recommend starting with the
                          "Translating Technical Information" LinkedIn Learning
                          course. Would you like me to add this to your learning
                          plan?
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
            {activeExample === 'hybrid' && <div data-id="element-673">
                <h2 className="text-xl font-medium text-gray-900 mb-4" data-id="element-674">
                  Hybrid Responses with References
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-675">
                  The Growth Agent can combine information from corporate
                  knowledge bases and external sources to provide comprehensive
                  answers with clear references.
                </p>
                <div className="border rounded-lg overflow-hidden mb-6" data-id="element-676">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-677">
                    <p className="text-sm font-medium" data-id="element-678">Example Query</p>
                  </div>
                  <div className="p-4 bg-blue-50" data-id="element-679">
                    <p className="text-blue-800" data-id="element-680">
                      "What are the best practices for implementing
                      microservices architecture in our organization?"
                    </p>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden" data-id="element-681">
                  <div className="bg-gray-50 px-4 py-2 border-b" data-id="element-682">
                    <p className="text-sm font-medium" data-id="element-683">Agent Response</p>
                  </div>
                  <div className="p-4" data-id="element-684">
                    <div className="flex items-start" data-id="element-685">
                      <div className="bg-blue-100 p-1.5 rounded-full mr-3" data-id="element-686">
                        <FileText className="w-5 h-5 text-blue-600" data-id="element-687" />
                      </div>
                      <div data-id="element-688">
                        <p className="text-gray-800 mb-4" data-id="element-689">
                          Here are the best practices for implementing
                          microservices architecture in your organization,
                          combining our internal guidelines with industry
                          standards:
                        </p>
                        <div className="space-y-4 mb-6" data-id="element-690">
                          <div className="bg-gray-50 border rounded-md p-4" data-id="element-691">
                            <h4 className="font-medium mb-2 flex items-center" data-id="element-692">
                              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded mr-2" data-id="element-693">
                                Internal
                              </span>
                              Service Boundaries
                            </h4>
                            <p className="text-gray-700 mb-2" data-id="element-694">
                              According to our Engineering Playbook, services
                              should be defined around business capabilities
                              rather than technical functions. This aligns with
                              Domain-Driven Design principles.
                            </p>
                            <div className="text-xs text-gray-500 italic" data-id="element-695">
                              Source: Engineering Playbook v3.2, Section 4.1
                              "Service Architecture"
                            </div>
                          </div>
                          <div className="bg-gray-50 border rounded-md p-4" data-id="element-696">
                            <h4 className="font-medium mb-2 flex items-center" data-id="element-697">
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded mr-2" data-id="element-698">
                                External
                              </span>
                              Data Management
                            </h4>
                            <p className="text-gray-700 mb-2" data-id="element-699">
                              Each microservice should own its data store. This
                              practice ensures loose coupling between services
                              and enables independent scaling and deployment.
                            </p>
                            <div className="text-xs text-gray-500 italic" data-id="element-700">
                              Source: "Building Microservices" by Sam Newman,
                              O'Reilly Media, 2021
                            </div>
                          </div>
                          <div className="bg-gray-50 border rounded-md p-4" data-id="element-701">
                            <h4 className="font-medium mb-2 flex items-center" data-id="element-702">
                              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded mr-2" data-id="element-703">
                                Internal
                              </span>
                              Deployment Strategy
                            </h4>
                            <p className="text-gray-700 mb-2" data-id="element-704">
                              Our DevOps team recommends containerizing all
                              microservices using Docker and orchestrating with
                              Kubernetes. This approach has been successful for
                              our payment processing and user management
                              systems.
                            </p>
                            <div className="text-xs text-gray-500 italic" data-id="element-705">
                              Source: Internal Case Study: "Microservices
                              Migration for Payment Platform" (2022)
                            </div>
                          </div>
                          <div className="bg-gray-50 border rounded-md p-4" data-id="element-706">
                            <h4 className="font-medium mb-2 flex items-center" data-id="element-707">
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded mr-2" data-id="element-708">
                                External
                              </span>
                              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded mr-2" data-id="element-709">
                                Internal
                              </span>
                              Monitoring and Observability
                            </h4>
                            <p className="text-gray-700 mb-2" data-id="element-710">
                              Implement comprehensive monitoring with
                              distributed tracing. Our SRE team uses Prometheus
                              and Jaeger, which aligns with industry best
                              practices for observability in distributed
                              systems.
                            </p>
                            <div className="text-xs text-gray-500 italic" data-id="element-711">
                              Sources: SRE Handbook (Internal), "Microservices
                              Observability Patterns" - InfoQ Article (2023)
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-800" data-id="element-712">
                          Based on both our internal experience and external
                          best practices, I recommend starting with a pilot
                          project to implement these patterns. The Platform Team
                          has documented a microservices starter template that
                          incorporates these practices.
                        </p>
                        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-md p-3" data-id="element-713">
                          <p className="text-sm text-blue-800 font-medium mb-2" data-id="element-714">
                            Recommended Next Steps:
                          </p>
                          <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1" data-id="element-715">
                            <li data-id="element-716">
                              Review the Microservices Starter Template in the
                              Platform Team's repository
                            </li>
                            <li data-id="element-717">
                              Schedule a consultation with Alex Chen (Platform
                              Architect) who led our payment system migration
                            </li>
                            <li data-id="element-718">
                              Attend the upcoming "Microservices at Scale"
                              workshop on July 8th
                            </li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
        <div className="border-t p-4 bg-gray-50" data-id="element-719">
          <p className="text-sm text-gray-600" data-id="element-720">
            Try asking the Growth Agent similar questions to get personalized
            responses based on your profile and needs.
          </p>
        </div>
      </div>
    </div>;
};
export default ExamplesModal;